import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../contexts/AuthContext';
import FirebaseService from '../services/firebaseService';
import { format, addDays } from 'date-fns';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, get, getDatabase } from 'firebase/database';
import JSZip from 'jszip';
import { useLocation } from 'react-router-dom';

interface JSZipMetadata {
  percent: number;
  currentFile: string | null;
}

const DataManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteTestDataDialogOpen, setDeleteTestDataDialogOpen] = useState(false);
  const [finalConfirmationDialogOpen, setFinalConfirmationDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [databaseExists, setDatabaseExists] = useState<boolean | null>(null);
  const location = useLocation();
  const isInProfile = location.pathname !== '/data-management';

  const firebaseService = FirebaseService.getInstance();

  // Memoize the checkForData function
  const checkForData = useCallback(async () => {
    if (!currentUser) {
      setError('You must be logged in to access this page.');
      setHasData(false);
      setDatabaseExists(false);
      setInitialLoading(false);
      return;
    }

    try {
      // Log current user and emulator state
      console.log('Checking data for user:', currentUser.uid);
      console.log('Emulator mode:', localStorage.getItem('useFirebaseEmulator'));
      console.log('Database URL:', getDatabase().app.options.databaseURL);

      // Only check canonical path
      const canonicalPath = `users/${currentUser.uid}/grids`;
      console.log('Checking canonical path:', canonicalPath);
      const dbRef = ref(getDatabase(), canonicalPath);
      const snapshot = await get(dbRef);

      if (snapshot.exists()) {
        // Data exists in the canonical path
        setDatabaseExists(true);
        setHasData(true);
        setError(null);
        console.log('Found data in canonical path:', snapshot.val());
      } else {
        console.log('No data found in canonical path.');
        setDatabaseExists(false);
        setHasData(false);
        setError('No test data found. Please complete your first test to create data.');
      }
    } catch (err) {
      console.error('Error checking data:', err);
      setDatabaseExists(false);
      setHasData(false);
      
      if (err instanceof Error) {
        if (err.message.includes('permission-denied')) {
          setError('You do not have permission to access this data.');
        } else if (err.message.includes('network')) {
          setError('Network error. Please check your connection.');
        } else if (err.message.includes('not-found')) {
          setError('No test data found. Please complete your first test.');
        } else {
          setError('Failed to check for test data.');
        }
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setInitialLoading(false);
    }
  }, [currentUser]);

  // Reset states when component unmounts
  useEffect(() => {
    return () => {
      setInitialLoading(false);
      setError(null);
      setSuccess(null);
      setHasData(null);
      setDatabaseExists(null);
    };
  }, []);

  // Handle initial data check
  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      if (!isMounted || !currentUser) return;

      try {
        await checkForData();
      } catch (err) {
        console.error('Error in initialization:', err);
        if (isMounted) {
          setError('Failed to initialize data management.');
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    // Remove the pathname check since this component is used in both contexts
    setInitialLoading(true);
    initializeData();

    return () => {
      isMounted = false;
    };
  }, [currentUser, checkForData]);

  const handleDownloadData = async () => {
    try {
      setDownloadingData(true);
      setDownloadProgress(0);
      setError(null);
      setSuccess(null);

      // Check if user is authenticated
      if (!currentUser) {
        throw new Error('You must be logged in to download test data.');
      }

      // Check database status first
      let isEmpty = false;
      try {
        isEmpty = await firebaseService.isDatabaseEmpty();
        if (isEmpty) {
          throw new Error('You currently have no test data to download.');
        }
      } catch (checkErr) {
        console.error('Error checking database status:', checkErr);
        throw new Error('Failed to verify test data status. Please try again.');
      }
      
      // Get all user grids
      const result = await firebaseService.getUserGrids({
        limit: 1000, // Set a high limit to get all grids
        isPremium: true // Ensure we get all data for download
      });
      
      const grids = result.grids;
      
      if (!grids || grids.length === 0) {
        throw new Error('No test data found to download.');
      }

      // Create a new zip file
      const zip = new JSZip();
      const imagesFolder = zip.folder("amsler_grid_tests");
      
      if (!imagesFolder) {
        throw new Error('Failed to create zip folder');
      }

      try {
        // Create a summary JSON file with all test data
        const summaryData = {
          totalTests: grids.length,
          exportDate: new Date().toISOString(),
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          tests: grids.map(grid => ({
            ...grid,
            imageUrl: undefined // Remove image URLs from JSON for privacy
          }))
        };
        
        // Add metadata file
        imagesFolder.file("test_data_summary.json", JSON.stringify(summaryData, null, 2));
        console.log(`Created summary file with ${grids.length} tests`);

        // Process each grid and generate image
        for (let i = 0; i < grids.length; i++) {
          const grid = grids[i];
          
          try {
            const imageDataUrl = firebaseService.generateGridImage(grid);
            
            if (!imageDataUrl) {
              console.warn(`Failed to generate image for grid ${grid.id}, skipping...`);
              continue;
            }
            
            // Convert data URL to blob
            const imageData = imageDataUrl.split(',')[1];
            const testDate = new Date(grid.timestamp);
            
            // Create filename with date and eye information
            const fileName = `test_${format(testDate, 'yyyy-MM-dd_HH-mm')}_${grid.eyeTested}.png`;
            
            // Add image to zip
            imagesFolder.file(fileName, imageData, { base64: true });
            
            // Update progress
            const progress = ((i + 1) / grids.length) * 100;
            setDownloadProgress(progress);
            console.log(`Processed grid ${i + 1}/${grids.length} (${Math.round(progress)}%)`);
          } catch (gridErr) {
            console.error(`Error processing grid ${grid.id}:`, gridErr);
            // Continue with other grids
          }
        }

        // Generate the zip file
        console.log('Generating final zip file...');
        const zipContent = await zip.generateAsync({ 
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: {
            level: 9
          }
        }, (metadata: JSZipMetadata) => {
          setDownloadProgress(metadata.percent);
        });

        if (!zipContent) {
          throw new Error('Failed to generate zip file');
        }

        // Create download link
        const url = window.URL.createObjectURL(zipContent);
        const a = document.createElement('a');
        a.href = url;
        a.download = `amsler_grid_tests_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.zip`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setSuccess('Your test data has been downloaded successfully.');
        setDownloadProgress(100);
        console.log('Download completed successfully');
      } catch (processErr) {
        console.error('Error processing data:', processErr);
        throw new Error('Failed to process and download data. Please try again.');
      }
    } catch (err) {
      console.error('Error downloading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to download data. Please try again.');
      setDownloadProgress(0);
    } finally {
      setDownloadingData(false);
    }
  };

  const handleDeleteTestData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Check if user is authenticated
      if (!currentUser) {
        setError('You must be logged in to delete test data.');
        return;
      }

      // Check database status
      let isEmpty = false;
      try {
        isEmpty = await firebaseService.isDatabaseEmpty();
        if (isEmpty) {
          setError('You have no test data to delete.');
          setFinalConfirmationDialogOpen(false);
          setDeleteTestDataDialogOpen(false);
          setHasData(false);
          return;
        }
      } catch (checkErr) {
        console.error('Error checking database status:', checkErr);
        setError('Failed to verify test data status. Please try again.');
        setFinalConfirmationDialogOpen(false);
        setDeleteTestDataDialogOpen(false);
        return;
      }

      // Attempt to delete all grids
      try {
        await firebaseService.deleteAllGrids();
        
        // Verify deletion was successful
        const isStillEmpty = await firebaseService.isDatabaseEmpty();
        if (!isStillEmpty) {
          throw new Error('Failed to delete all test data. Some data may remain.');
        }
        
        // Update UI state
        setSuccess('Your test data has been successfully deleted.');
        setFinalConfirmationDialogOpen(false);
        setDeleteTestDataDialogOpen(false);
        setHasData(false);
        
        // Log success
        console.log('Successfully deleted all test data');
      } catch (deleteErr) {
        console.error('Error deleting test data:', deleteErr);
        throw new Error('Failed to delete test data completely. Please try again.');
      }
    } catch (err) {
      console.error('Error in handleDeleteTestData:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete test data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const functions = getFunctions();
      const initiateDeletionRequest = httpsCallable(functions, 'initiateDeletionRequest');
      const result = await initiateDeletionRequest({ isAccountDeletion: true });
      const { success, message, scheduledDeletionDate } = (result.data as any);
      
      if (success) {
        setSuccess(`Your account has been scheduled for deletion. It will be permanently deleted on ${format(new Date(scheduledDeletionDate), 'MMMM d, yyyy')}.`);
      } else {
        setError(message || 'Failed to schedule account deletion.');
      }
      setDeleteAccountDialogOpen(false);
    } catch (err) {
      console.error('Error scheduling account deletion:', err);
      setError('Failed to schedule account deletion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        py: isInProfile ? 2 : 8 
      }}>
        <CircularProgress size={40} sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Checking Data Status
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Please wait while we verify your data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isInProfile ? 0 : 4 }}>
      {!isInProfile && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <DeleteIcon sx={{ fontSize: 32, mr: 2, color: 'error.main' }} />
          <Typography variant="h4" component="h1" sx={{ color: 'text.primary' }}>
            Delete Your Data
          </Typography>
        </Box>
      )}

      {!isInProfile && (
        <Typography variant="body1" sx={{ mb: 4, color: 'text.primary' }}>
          You can delete your test data while keeping your account, or delete your entire account including all data.
        </Typography>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            backgroundColor: 'rgba(211, 47, 47, 0.1)',
            color: '#d32f2f',
            '& .MuiAlert-icon': {
              color: '#d32f2f'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
        >
          {success}
        </Alert>
      )}

      {!databaseExists && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No database found. Please complete your first test to create data.
        </Alert>
      )}

      {databaseExists && hasData === false && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your database exists but contains no test data to delete.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: 'warning.main', 
              mr: 2 
            }} />
            Delete Test Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
            Immediately deletes all your test data while keeping your account active
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: 'error.main', 
              mr: 2 
            }} />
            Delete Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
            Schedules your account for deletion with a 15-day retention period (until {format(addDays(new Date(), 15), 'MMMM d, yyyy')})
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (databaseExists && hasData) setDeleteTestDataDialogOpen(true);
            }}
            disabled={loading || !databaseExists || !hasData}
            startIcon={<DeleteIcon />}
            fullWidth
          >
            {!databaseExists ? 'No Database Found' : !hasData ? 'No Test Data' : 'Delete All My Test Data'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteAccountDialogOpen(true)}
            disabled={loading}
            startIcon={<DeleteIcon />}
            fullWidth
          >
            Delete My Account & All Data
          </Button>
        </Box>
      </Box>

      {/* First Delete Test Data Dialog */}
      <Dialog
        open={deleteTestDataDialogOpen}
        onClose={() => !downloadingData && setDeleteTestDataDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Download and Delete Test Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Before deleting your data, you can download a copy of all your test history.
            Each test will be saved as an image file with all the information included.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {downloadingData && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                  Preparing your data... {Math.round(downloadProgress)}%
                </Typography>
                <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Box
                    sx={{
                      width: `${downloadProgress}%`,
                      height: 4,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      transition: 'width 0.3s ease-in-out',
                    }}
                  />
                </Box>
              </Box>
            )}
            <Button
              onClick={handleDownloadData}
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              disabled={downloadingData || !databaseExists || !hasData}
              fullWidth
            >
              {downloadingData ? 'Preparing Download...' : 
               !databaseExists ? 'No Database Found' :
               !hasData ? 'No Data to Download' : 
               'Download All Test Data'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteTestDataDialogOpen(false)} 
            disabled={downloadingData}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteTestDataDialogOpen(false);
              setFinalConfirmationDialogOpen(true);
            }}
            color="warning" 
            variant="contained"
            disabled={downloadingData || !databaseExists || !hasData}
          >
            {!databaseExists ? 'No Database Found' :
             !hasData ? 'No Data to Delete' : 
             'Continue to Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Confirmation Dialog */}
      <Dialog
        open={finalConfirmationDialogOpen}
        onClose={() => setFinalConfirmationDialogOpen(false)}
      >
        <DialogTitle>Final Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you absolutely sure you want to delete all your test data? This action cannot be undone.
            All your test history and results will be permanently deleted.
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This action is available to all users including free users and those with expired subscriptions.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Note: Free users and users with expired subscriptions are still limited to 10 total tests. Deleting all tests will not reset this limit as deleted tests are counted toward your total.
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
            This action is permanent and cannot be reversed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFinalConfirmationDialogOpen(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTestData} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Yes, Delete All My Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteAccountDialogOpen}
        onClose={() => setDeleteAccountDialogOpen(false)}
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account and all associated data? This action cannot be undone.
            Your account will be scheduled for deletion and will be permanently deleted after a 15-day retention period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteAccountDialogOpen(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement; 